import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronDown, Menu, X, Layers, Shield, FileText, Calendar, ListTree, BarChart3,
  Sun, Wind, Zap, Thermometer, Atom, Flame, Map, Users, Building2, Briefcase, Newspaper,
  QrCode, ArrowRight,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { AnnounceBar } from "@/components/AnnounceBar";
import { OM_MODULE, OM_SECTOR_LINKS } from "@/lib/omNav";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useTheme } from "@/context/ThemeContext";
import { cn } from "@/lib/utils";

const PLATFORM = [
  [Layers, "Features", "/features"],
  [Shield, "Risk Radar", "/features#risk-radar"],
  [FileText, "BOQ Parser", "/features#boq-parser"],
  [Calendar, "DPR Management", "/features#dpr"],
  [ListTree, "WBS Tracker", "/features#wbs"],
  [BarChart3, "Predictive Analytics", "/features#predictive-analytics"],
];

const INDUSTRY = [
  [Sun, "Solar EPC", "/industry/solar-epc"],
  [Wind, "Wind EPC", "/industry/wind-epc"],
  [Zap, "Engineering EPC", "/industry/engineering-epc"],
  [Thermometer, "HVAC", "/industry/hvac"],
  [Atom, "Nuclear Projects", "/industry/nuclear-projects"],
  [Flame, "Thermal Power Plant", "/industry/thermal-power-plant"],
  [Map, "Other Infrastructure", "/industry/other-infrastructure"],
];

const COMPANY = [
  [Building2, "About Us", "/about"],
  [Users, "Our Team", "/about#team"],
  [Briefcase, "Careers", "/careers"],
  [Newspaper, "Blog", "/blog"],
];

const MENUS = [["Platform", PLATFORM], ["Industry", INDUSTRY], ["Company", COMPANY]];

const GOLD = "#C9A84C";
const NAVY = "#0A1628";
const DROP = "#0D1B2A";
const MUTED = "#8892B0";

const OmMega = ({ open, onToggle, active, dark }) => (
  <div className="relative">
    <button
      onClick={onToggle}
      className="flex items-center gap-1.5 text-[15px] font-medium py-2 transition-colors duration-200 hover:!text-[#C9A84C]"
      style={{ color: active || open ? GOLD : dark ? "#FFFFFF" : NAVY }}
      data-testid="nav-o&m-trigger"
    >
      O&amp;M
      <ChevronDown className={cn("w-3.5 h-3.5 transition-transform duration-200", open && "rotate-180")} />
    </button>
    {active ? <span className="absolute -bottom-1 left-0 right-4 h-0.5 bg-[#C9A84C]" /> : null}
    {open ? (
      <div
        className="absolute left-0 top-full mt-3 w-[560px] xl:w-[640px] max-w-[calc(100vw-3rem)] rounded-lg border overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 z-50"
        style={{ background: DROP, borderColor: "rgba(201,168,76,0.28)", boxShadow: "0 24px 55px rgba(0,0,0,0.55)" }}
        data-testid="nav-om-menu"
      >
        <div className="grid grid-cols-2 gap-x-2 p-4">
          <div>
            <p className="px-3 pb-2 text-[13px] font-semibold uppercase tracking-[0.14em]" style={{ color: GOLD }}>
              O&amp;M Module
            </p>
            {OM_MODULE.map(([Icon, text, to, desc]) => (
              <Link
                key={to}
                to={to}
                className="group flex items-start gap-2.5 px-3 py-2 rounded transition-colors duration-150 hover:bg-[#C9A84C]/10"
                style={{ borderLeft: "2px solid transparent" }}
                onMouseEnter={(e) => { e.currentTarget.style.borderLeftColor = GOLD; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderLeftColor = "transparent"; }}
                data-testid={`nav-om-${to.replace("/om", "").replace("/", "") || "dashboard"}`}
              >
                <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: GOLD }} />
                <span className="min-w-0">
                  <span className="block text-[13.5px] text-white group-hover:text-[#C9A84C] transition-colors duration-150">{text}</span>
                  <span className="block text-[11.5px] leading-snug mt-0.5" style={{ color: MUTED }}>{desc}</span>
                </span>
              </Link>
            ))}
          </div>
          <div>
            <p className="px-3 pb-2 text-[13px] font-semibold uppercase tracking-[0.14em]" style={{ color: GOLD }}>
              Sectors
            </p>
            {OM_SECTOR_LINKS.map(([text, slug]) => (
              <Link
                key={slug}
                to={`/om/sectors/${slug}`}
                className="block px-3 py-[7px] text-[13px] text-white rounded transition-colors duration-150 hover:bg-[#C9A84C]/10 hover:!text-[#C9A84C]"
                style={{ borderLeft: "2px solid transparent" }}
                onMouseEnter={(e) => { e.currentTarget.style.borderLeftColor = GOLD; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderLeftColor = "transparent"; }}
                data-testid={`nav-om-sector-${slug}`}
              >
                {text}
              </Link>
            ))}
          </div>
        </div>
        <Link
          to="/om/assets"
          className="flex items-center gap-2 px-4 py-2.5 text-[12.5px] font-semibold transition-colors duration-150 hover:brightness-95"
          style={{ background: GOLD, color: NAVY }}
          data-testid="nav-om-qr-cta"
        >
          <QrCode className="w-4 h-4 shrink-0" />
          <span className="flex-1">New: Asset QR Code scanning — log readings and raise faults from the field in seconds.</span>
          <span className="inline-flex items-center gap-1 shrink-0 underline">Learn More <ArrowRight className="w-3 h-3" /></span>
        </Link>
      </div>
    ) : null}
  </div>
);

const Dropdown = ({ label, items, open, onToggle, active, dark }) => (
  <div className="relative">
    <button
      onClick={onToggle}
      className="flex items-center gap-1.5 text-[15px] font-medium py-2 transition-colors duration-200 hover:!text-[#C9A84C]"
      style={{ color: active || open ? GOLD : dark ? "#FFFFFF" : NAVY }}
      data-testid={`nav-${label.toLowerCase()}-trigger`}
    >
      {label}
      <ChevronDown className={cn("w-3.5 h-3.5 transition-transform duration-200", open && "rotate-180")} />
    </button>
    {active ? <span className="absolute -bottom-1 left-0 right-4 h-0.5 bg-[#C9A84C]" /> : null}
    {open ? (
      <div
        className="absolute left-0 top-full mt-3 w-[268px] rounded-lg border shadow-2xl py-2 animate-in fade-in slide-in-from-top-1 duration-200 z-50"
        style={{
          background: dark ? "#112240" : "#FFFFFF",
          borderColor: dark ? "rgba(255,255,255,0.1)" : "rgba(10,22,40,0.1)",
          boxShadow: dark ? "0 20px 45px rgba(0,0,0,0.5)" : "0 18px 40px rgba(10,22,40,0.14)",
        }}
        data-testid={`nav-${label.toLowerCase()}-menu`}
      >
        {items.map(([Icon, text, to]) => (
          <Link
            key={to}
            to={to}
            className="flex items-center gap-2.5 px-4 py-2.5 text-[14px] transition-colors duration-150 hover:!text-[#C9A84C] hover:bg-[#C9A84C]/10"
            style={{ color: dark ? "rgba(255,255,255,0.85)" : NAVY }}
            data-testid={`nav-item-${text.replace(/\s/g, "-").toLowerCase()}`}
          >
            <Icon className="w-4 h-4 shrink-0" style={{ color: GOLD }} />
            {text}
          </Link>
        ))}
      </div>
    ) : null}
  </div>
);

export default function PublicHeader() {
  const { pathname, hash } = useLocation();
  const { dark } = useTheme();
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(null); setMobile(false); }, [pathname, hash]);

  useEffect(() => {
    const close = (e) => { if (!e.target.closest("[data-nav-root]")) setOpen(null); };
    const esc = (e) => { if (e.key === "Escape") { setOpen(null); setMobile(false); } };
    document.addEventListener("click", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("click", close); document.removeEventListener("keydown", esc); };
  }, []);

  const isActive = (items) => items.some(([, , to]) => pathname === to.split("#")[0] && to !== "/");
  const linkColor = (on) => ({ color: on ? GOLD : dark ? "#FFFFFF" : NAVY });
  const surface = dark ? "#0A1628" : "#FFFFFF";

  return (
    <header
      data-nav-root
      className="sticky top-0 z-50 border-b"
      style={{
        background: scrolled ? `${surface}F7` : surface,
        backdropFilter: scrolled ? "blur(12px)" : "none",
        borderColor: dark ? "rgba(201,168,76,0.2)" : "rgba(10,22,40,0.08)",
        boxShadow: dark ? "none" : "0 1px 3px rgba(10,22,40,0.08)",
        color: dark ? "#FFFFFF" : NAVY,
        transition: "background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease",
      }}
      data-testid="public-header"
    >
      <div className="h-14 md:h-16 max-w-[1280px] mx-auto px-4 sm:px-6 flex items-center gap-8">
        <Link to="/" data-testid="header-logo-link">
          {dark ? <Logo mono size="sm" className="text-white" /> : <Logo size="sm" />}
        </Link>

        <nav className="hidden md:flex items-center gap-7" data-testid="header-nav">
          <Link
            to="/"
            className="relative text-[15px] font-medium py-2 transition-colors duration-200 hover:!text-[#C9A84C]"
            style={linkColor(pathname === "/")}
            data-testid="nav-home-link"
          >
            Home
            {pathname === "/" ? <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#C9A84C]" /> : null}
          </Link>
          {MENUS.map(([label, items]) => (
            <div key={label} className="contents">
              <Dropdown
                label={label}
                items={items}
                dark={dark}
                open={open === label}
                active={isActive(items)}
                onToggle={() => setOpen(open === label ? null : label)}
              />
              {label === "Platform" ? (
                <OmMega
                  dark={dark}
                  open={open === "O&M"}
                  active={pathname.startsWith("/om")}
                  onToggle={() => setOpen(open === "O&M" ? null : "O&M")}
                />
              ) : null}
            </div>
          ))}
          <Link
            to="/contact"
            className="relative text-[15px] font-medium py-2 transition-colors duration-200 hover:!text-[#C9A84C]"
            style={linkColor(pathname === "/contact")}
            data-testid="nav-contact-link"
          >
            Contact Us
            {pathname === "/contact" ? <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#C9A84C]" /> : null}
          </Link>
        </nav>

        <div className="ml-auto flex items-center gap-2.5">
          <ThemeToggle className="hidden md:inline-flex" />
          <Link
            to="/login"
            className="hidden sm:inline-flex items-center h-9 px-4 rounded-lg border text-[14px] font-medium transition-colors duration-200"
            style={{
              borderColor: dark ? "rgba(255,255,255,0.4)" : NAVY,
              color: dark ? "#FFFFFF" : NAVY,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = dark ? "#FFFFFF" : NAVY;
              e.currentTarget.style.color = dark ? NAVY : "#FFFFFF";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = dark ? "#FFFFFF" : NAVY;
            }}
            data-testid="header-login-btn"
          >
            Login
          </Link>
          <Link
            to="/signup"
            className="inline-flex items-center h-9 px-4 rounded-lg text-[14px] font-semibold text-white transition-colors duration-200"
            style={{ backgroundColor: GOLD }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#B8973A"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = GOLD; }}
            data-testid="header-trial-btn"
          >
            Start Free Trial
          </Link>
          <button
            onClick={() => setMobile(!mobile)}
            className="md:hidden p-1.5"
            style={{ color: GOLD }}
            aria-label="Menu"
            data-testid="header-hamburger-btn"
          >
            {mobile ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnnounceBar />

      {mobile ? (
        <div
          className="md:hidden absolute inset-x-0 top-full border-b max-h-[calc(100vh-6.25rem)] overflow-y-auto animate-in slide-in-from-top-2 duration-200"
          style={{
            background: surface,
            borderColor: dark ? "rgba(201,168,76,0.2)" : "rgba(10,22,40,0.1)",
            boxShadow: dark ? "none" : "0 12px 30px rgba(10,22,40,0.12)",
          }}
          data-testid="mobile-menu"
        >
          <div
            className="flex items-center justify-between px-5 py-2.5 border-b"
            style={{ borderColor: dark ? "rgba(255,255,255,0.06)" : "rgba(10,22,40,0.07)" }}
          >
            <span className="text-[11px] uppercase tracking-[0.16em]" style={{ color: GOLD }}>Appearance</span>
            <ThemeToggle />
          </div>
          <Link
            to="/"
            className="block px-5 py-3.5 text-[15px] border-b"
            style={{ ...linkColor(false), borderColor: dark ? "rgba(255,255,255,0.05)" : "rgba(10,22,40,0.06)" }}
            data-testid="mobile-nav-home"
          >
            Home
          </Link>
          {MENUS.map(([label, items]) => (
            <div key={label} className="contents">
              <div className="border-b" style={{ borderColor: dark ? "rgba(255,255,255,0.05)" : "rgba(10,22,40,0.06)" }}>
                <p className="px-5 pt-4 pb-1 text-[11px] uppercase tracking-[0.16em]" style={{ color: GOLD }}>{label}</p>
                {items.map(([Icon, text, to]) => (
                  <Link
                    key={to}
                    to={to}
                    className="flex items-center gap-2.5 px-5 py-2.5 text-[14px]"
                    style={{ color: dark ? "rgba(255,255,255,0.85)" : NAVY }}
                    data-testid={`mobile-nav-${text.replace(/\s/g, "-").toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4" style={{ color: GOLD }} /> {text}
                  </Link>
                ))}
              </div>
              {label === "Platform" ? (
                <div className="border-b" style={{ borderColor: dark ? "rgba(255,255,255,0.05)" : "rgba(10,22,40,0.06)" }} data-testid="mobile-nav-om-group">
                  <p className="px-5 pt-4 pb-1 text-[11px] uppercase tracking-[0.16em]" style={{ color: GOLD }}>O&amp;M Module</p>
                  {OM_MODULE.map(([Icon, text, to]) => (
                    <Link
                      key={to}
                      to={to}
                      className="flex items-center gap-2.5 px-5 py-2.5 text-[14px]"
                      style={{ color: dark ? "rgba(255,255,255,0.85)" : NAVY }}
                      data-testid={`mobile-nav-om-${to.replace("/om", "").replace("/", "") || "dashboard"}`}
                    >
                      <Icon className="w-4 h-4" style={{ color: GOLD }} /> {text}
                    </Link>
                  ))}
                  <p className="px-5 pt-3 pb-1 text-[11px] uppercase tracking-[0.16em]" style={{ color: GOLD }}>Sectors</p>
                  {OM_SECTOR_LINKS.map(([text, slug]) => (
                    <Link
                      key={slug}
                      to={`/om/sectors/${slug}`}
                      className="block px-5 py-2 text-[13.5px]"
                      style={{ color: dark ? "rgba(255,255,255,0.85)" : NAVY }}
                      data-testid={`mobile-nav-om-sector-${slug}`}
                    >
                      {text}
                    </Link>
                  ))}
                  <Link to="/om/assets" className="flex items-center gap-2 mx-5 my-3 px-3 py-2 rounded text-[12px] font-semibold"
                        style={{ background: GOLD, color: NAVY }} data-testid="mobile-nav-om-qr-cta">
                    <QrCode className="w-3.5 h-3.5 shrink-0" /> New: Asset QR Code scanning — Learn More
                  </Link>
                </div>
              ) : null}
            </div>
          ))}
          <Link to="/contact" className="block px-5 py-3.5 text-[15px]" style={linkColor(false)} data-testid="mobile-nav-contact">
            Contact Us
          </Link>
          <div className="p-5 pt-1">
            <Link
              to="/login"
              className="block text-center h-10 leading-10 rounded-lg border text-[14px]"
              style={{ borderColor: dark ? "rgba(255,255,255,0.4)" : NAVY, color: dark ? "#FFFFFF" : NAVY }}
              data-testid="mobile-login-btn"
            >
              Login
            </Link>
          </div>
        </div>
      ) : null}
    </header>
  );
}
